import Phaser from 'phaser';

export type ShapeKind =
  | 'triangle'
  | 'square'
  | 'pentagon'
  | 'hexagon'
  | 'octagon'
  | 'star'
  | 'heart'
  | 'crescent'
  | 'plus'
  | 'circle';

export interface Tier {
  index: number;
  kind: ShapeKind;
  label: string;
  radius: number;
  color: number;
  score: number;
}

// 10-tier evolution chain. Every tier has a unique shape and a unique color so
// the player never confuses two adjacent (or nearby) bodies in the pile.
// Radii ascend monotonically so the visual progression matches the gameplay
// progression.
//
// Sizing (playtest feedback 06-2026): shapes grew ~20% from the original curve
// (20/25/31/39/49/61/76/95/119/149). The top tiers taper off (star +14%,
// octagon +6%, KING -3%) so the KING's diameter (288) still fits the narrowest
// fully-visible mobile container (~292px play width on a 360px screen) — see
// the playW calculation in GameScene.
// ~32% larger shapes on tablet-width screens (≥700px): 1.15 from commit 8248cfc
// increased a further 15% to match the wider/taller tablet container.
export const TABLET_SHAPE_SCALE = 1.32;
let _tierScale = 1.0;
export function setTierScale(s: number): void { _tierScale = s; }
export function getTierScale(): number { return _tierScale; }

export const TIERS: Tier[] = [
  { index: 0, kind: 'triangle', label: 'Triangle', radius: 24,  color: 0xff6aa8, score: 1 },
  { index: 1, kind: 'square',   label: 'Square',   radius: 30,  color: 0xffa62b, score: 3 },
  { index: 2, kind: 'heart',    label: 'Heart',    radius: 37,  color: 0xff4d5e, score: 6 },
  { index: 3, kind: 'pentagon', label: 'Pentagon', radius: 47,  color: 0x2fd1b6, score: 12 },
  { index: 4, kind: 'crescent', label: 'Crescent', radius: 59,  color: 0x45b7ff, score: 20 },
  { index: 5, kind: 'hexagon',  label: 'Hexagon',  radius: 73,  color: 0x8a5cff, score: 30 },
  { index: 6, kind: 'plus',     label: 'Plus',     radius: 91,  color: 0xcf6bff, score: 45 },
  { index: 7, kind: 'star',     label: 'Star',     radius: 108, color: 0xffd23f, score: 65 },
  { index: 8, kind: 'octagon',  label: 'Octagon',  radius: 126, color: 0x8fe05a, score: 90 },
  { index: 9, kind: 'circle',   label: 'KING',     radius: 144, color: 0xff3d6e, score: 200 },
];

// Spawnable pool: tiers 0–4 (triangle through crescent) drop from the queue.
// Hexagon and above only appear as merge results.
export const SPAWNABLE_TIER_INDICES = [0, 1, 2, 3, 4] as const;

/**
 * Effective physics radius per shape kind, as a fraction of the tier's visual
 * radius. The visual radius is the bounding-circle radius the texture is
 * drawn in; the physics radius should approximate the shape's actual visible
 * footprint so two colliding bodies don't leave a gap between their
 * silhouettes. Tuned by eye — hearts/crescents/pluses/stars have lots of
 * empty space inside their bounding circle, so their physics radius shrinks.
 */
const PHYSICS_FACTOR: Record<ShapeKind, number> = {
  circle:   1.0,
  octagon:  0.95,
  hexagon:  0.92,
  pentagon: 0.9,
  square:   0.84,
  triangle: 0.72,
  heart:    0.82,
  crescent: 0.88,
  star:     0.7,
  plus:     0.68,
};

export function physicsRadiusFor(kind: ShapeKind, radius: number): number {
  return radius * PHYSICS_FACTOR[kind];
}

export const TEXTURE_PREFIX = 'shmerge-shape-';
export const textureKey = (tierIndex: number) => `${TEXTURE_PREFIX}${tierIndex}`;

export function polygonVerts(kind: ShapeKind, radius: number): { x: number; y: number }[] {
  switch (kind) {
    case 'triangle':
      return regularPolygon(3, radius, -Math.PI / 2);
    case 'square':
      return regularPolygon(4, radius, Math.PI / 4);
    case 'pentagon':
      return regularPolygon(5, radius, -Math.PI / 2);
    case 'hexagon':
      return regularPolygon(6, radius, 0);
    case 'octagon':
      return regularPolygon(8, radius, Math.PI / 8);
    case 'star':
      return starVerts(5, radius, radius * 0.45);
    case 'circle':
      return regularPolygon(24, radius, 0);
    default:
      // heart, crescent, plus use custom rendering — no polygon path.
      return [];
  }
}

function regularPolygon(sides: number, radius: number, rotation: number) {
  const verts: { x: number; y: number }[] = [];
  for (let i = 0; i < sides; i++) {
    const a = rotation + (i * Math.PI * 2) / sides;
    verts.push({ x: Math.cos(a) * radius, y: Math.sin(a) * radius });
  }
  return verts;
}

function starVerts(points: number, outer: number, inner: number) {
  const verts: { x: number; y: number }[] = [];
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = -Math.PI / 2 + (i * Math.PI) / points;
    verts.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
  }
  return verts;
}

// ─── Candy texture generation ─────────────────────────────────────────────────
// Every tier is pre-rendered as a glossy candy via canvas2d (Phaser Graphics
// has no radial gradients) and clipped to the exact procedural silhouette so
// the visual matches the collider. The shipping material is "jewel": a faceted
// translucent gem with a glowing core, a glint, and a light/dark bevel rim.
// (The earlier enamel/gummy skins and the in-game skin picker were retired
// after playtesting — jewel won.)

function rgbOf(color: number): { r: number; g: number; b: number } {
  return { r: (color >> 16) & 0xff, g: (color >> 8) & 0xff, b: color & 0xff };
}
function clamp255(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}
function hexOf(color: number): string {
  return '#' + (color & 0xffffff).toString(16).padStart(6, '0');
}
/** Lerp a color toward white by amt (0..1). */
function lightenC(color: number, amt: number): number {
  const { r, g, b } = rgbOf(color);
  return (clamp255(r + (255 - r) * amt) << 16) | (clamp255(g + (255 - g) * amt) << 8) | clamp255(b + (255 - b) * amt);
}
/** Lerp a color toward black by amt (0..1). */
function darkenC(color: number, amt: number): number {
  const { r, g, b } = rgbOf(color);
  return (clamp255(r * (1 - amt)) << 16) | (clamp255(g * (1 - amt)) << 8) | clamp255(b * (1 - amt));
}
/** Push each channel away from the pixel's mean by amt — boosts saturation. */
function saturateC(color: number, amt: number): number {
  const { r, g, b } = rgbOf(color);
  const m = (r + g + b) / 3;
  return (clamp255(r + (r - m) * amt) << 16) | (clamp255(g + (g - m) * amt) << 8) | clamp255(b + (b - m) * amt);
}
function rgbaOf(color: number, a: number): string {
  const { r, g, b } = rgbOf(color);
  return `rgba(${r},${g},${b},${a})`;
}

/** Erode the silhouette mask (full mask minus a copy scaled down so a rim of
 *  width `rimW` survives at the edge), then flood it with `fillCss`. rForK
 *  controls how far in the inner cut sits; the outer edge always hugs the mask
 *  so stacking two rings (dark outer + bright inner) builds a bevel. */
function erodeRing(
  mask: HTMLCanvasElement,
  size: number,
  cx: number,
  cy: number,
  rForK: number,
  rimW: number,
  fillCss: string,
): HTMLCanvasElement {
  const ring = document.createElement('canvas');
  ring.width = size;
  ring.height = size;
  const rctx = ring.getContext('2d')!;
  rctx.drawImage(mask, 0, 0);
  const k = (rForK - rimW) / rForK;
  const dw = size * k;
  rctx.globalCompositeOperation = 'destination-out';
  rctx.drawImage(mask, cx - dw / 2, cy - dw / 2, dw, dw);
  rctx.globalCompositeOperation = 'source-in';
  rctx.fillStyle = fillCss;
  rctx.fillRect(0, 0, size, size);
  rctx.globalCompositeOperation = 'source-over';
  return ring;
}

/** Faceted translucent gem: angular facets catching light, a glowing core, a
 *  diagonal glint, and a light-top/dark-bottom beveled rim. */
function paintJewelMaterial(
  octx: CanvasRenderingContext2D,
  mask: HTMLCanvasElement,
  tier: Tier,
  r: number,
  cx: number,
  cy: number,
  size: number,
): void {
  const color = saturateC(tier.color, 0.12);
  octx.fillStyle = hexOf(darkenC(color, 0.12));
  octx.fillRect(0, 0, size, size);

  const FACETS = 10;
  const lightDir = -Math.PI * 0.62;
  const reach = r * 1.7;
  const ox = cx - r * 0.12;
  const oy = cy - r * 0.14;
  for (let i = 0; i < FACETS; i++) {
    const a0 = (i / FACETS) * Math.PI * 2;
    const a1 = ((i + 1) / FACETS) * Math.PI * 2;
    const am = (a0 + a1) / 2;
    const d = Math.cos(am - lightDir);
    const lit = 0.5 + 0.5 * d;
    const shade = lit > 0.5 ? lightenC(color, (lit - 0.5) * 0.9) : darkenC(color, (0.5 - lit) * 0.55);
    octx.beginPath();
    octx.moveTo(ox, oy);
    octx.lineTo(ox + Math.cos(a0) * reach, oy + Math.sin(a0) * reach);
    octx.lineTo(ox + Math.cos(a1) * reach, oy + Math.sin(a1) * reach);
    octx.closePath();
    octx.fillStyle = hexOf(shade);
    octx.fill();
  }

  octx.globalCompositeOperation = 'lighter';
  const core = octx.createRadialGradient(ox, oy, r * 0.04, ox, oy, r * 0.85);
  core.addColorStop(0, rgbaOf(lightenC(color, 0.7), 0.85));
  core.addColorStop(0.4, rgbaOf(lightenC(color, 0.25), 0.35));
  core.addColorStop(1, rgbaOf(color, 0));
  octx.fillStyle = core;
  octx.fillRect(0, 0, size, size);
  octx.globalCompositeOperation = 'source-over';

  octx.save();
  octx.translate(cx - r * 0.3, cy - r * 0.42);
  octx.rotate(-0.7);
  const gl = octx.createLinearGradient(0, -r * 0.5, 0, r * 0.5);
  gl.addColorStop(0, 'rgba(255,255,255,0)');
  gl.addColorStop(0.5, 'rgba(255,255,255,0.9)');
  gl.addColorStop(1, 'rgba(255,255,255,0)');
  octx.fillStyle = gl;
  octx.beginPath();
  octx.ellipse(0, 0, r * 0.1, r * 0.5, 0, 0, Math.PI * 2);
  octx.fill();
  octx.restore();
  octx.fillStyle = 'rgba(255,255,255,0.95)';
  octx.beginPath();
  octx.arc(cx - r * 0.36, cy - r * 0.44, r * 0.07, 0, Math.PI * 2);
  octx.fill();

  octx.globalCompositeOperation = 'destination-in';
  octx.drawImage(mask, 0, 0);
  octx.globalCompositeOperation = 'source-over';

  const rimW = Math.max(1.8, r * 0.07);
  const rim = erodeRing(mask, size, cx, cy, r, rimW, '#ffffff');
  const rc = rim.getContext('2d')!;
  rc.globalCompositeOperation = 'source-in';
  const rg = rc.createLinearGradient(0, cy - r, 0, cy + r);
  rg.addColorStop(0, hexOf(lightenC(color, 0.95)));
  rg.addColorStop(0.5, rgbaOf(lightenC(color, 0.6), 0.5));
  rg.addColorStop(0.55, rgbaOf(darkenC(color, 0.4), 0.5));
  rg.addColorStop(1, hexOf(darkenC(color, 0.5)));
  rc.fillStyle = rg;
  rc.fillRect(0, 0, size, size);
  rc.globalCompositeOperation = 'source-over';
  octx.drawImage(rim, 0, 0);
}

// Supersample factor for the candy bake. The whole shape — silhouette mask,
// material, and beveled rim — is rendered at SS× the final texture resolution
// then high-quality downscaled, so every edge gets true multi-sample coverage
// instead of the hard ~1px staircase a single canvas `fill()` leaves. This is
// what kills the pixelated edges, worst-felt on the small tiers. Geometry is
// unchanged: each coordinate scales by SS and the downscale divides it back, so
// the visible silhouette still matches `_drawProceduralMask` / the colliders.
const SUPERSAMPLE = 2;

function generateStyledTexture(scene: Phaser.Scene, key: string, tier: Tier): void {
  // The final canvas lives at `logical` game pixels — NOT logical * DPR.
  // Phaser sets frame.realWidth from the canvas pixel size, and sizes sprites
  // by realWidth. If the canvas were DPR-scaled (e.g. 3× on Android) the
  // shapes would be displayed 3× too large in the game's CSS-pixel coordinate
  // space. We get anti-aliased edges via SUPERSAMPLE instead of DPR scaling.
  const logical = tier.radius * 2 + 6;

  // Render at SUPERSAMPLE× then downscale into a logical-pixel canvas.
  const work = logical * SUPERSAMPLE;
  const r = tier.radius * SUPERSAMPLE;
  const cx = work / 2;
  const cy = work / 2;

  // Silhouette mask (opaque white) — reuses the collider-aligned geometry.
  const mask = document.createElement('canvas');
  mask.width = work;
  mask.height = work;
  _drawProceduralMask(mask.getContext('2d')!, tier, r, cx, cy);

  // Candy material clipped to the silhouette, painted at the supersampled size.
  const hi = document.createElement('canvas');
  hi.width = work;
  hi.height = work;
  const hctx = hi.getContext('2d')!;
  paintJewelMaterial(hctx, mask, tier, r, cx, cy, work);

  // High-quality downscale into the final canvas.
  const out = document.createElement('canvas');
  out.width = logical;
  out.height = logical;
  const octx = out.getContext('2d')!;
  octx.imageSmoothingEnabled = true;
  octx.imageSmoothingQuality = 'high';
  octx.drawImage(hi, 0, 0, work, work, 0, 0, logical, logical);

  // Live re-skin: repaint backing canvas in place so all referencing sprites
  // update without a restart. Otherwise create the texture fresh.
  const existing = scene.textures.exists(key) ? scene.textures.get(key) : null;
  const existingCanvas = existing?.getSourceImage() as HTMLCanvasElement | undefined;
  if (existing && existingCanvas?.getContext && existingCanvas.width === logical && existingCanvas.height === logical) {
    const dctx = existingCanvas.getContext('2d')!;
    dctx.clearRect(0, 0, logical, logical);
    dctx.drawImage(out, 0, 0);
    (existing as Phaser.Textures.CanvasTexture).refresh();
    return;
  }
  if (existing) scene.textures.remove(key);
  scene.textures.addCanvas(key, out);
  scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
}

/**
 * Pre-render every tier as a candy texture. Cached per game (Phaser textures
 * are global), so this runs once across the whole session and is reused on
 * scene restarts — preserving the existing pooling/perf behavior.
 */
export function ensureTierTextures(scene: Phaser.Scene): void {
  for (const tier of TIERS) {
    const key = textureKey(tier.index);
    if (scene.textures.exists(key)) continue;
    generateStyledTexture(scene, key, tier);
  }
}

// ─── External Imagen image pipeline ──────────────────────────────────────────

// How much of the Imagen JPEG's width is occupied by the shape body.
// Used to size the canvas so the shape diameter equals tier.radius * 2.
const FILL_FACTOR = 0.78;

export function queueShapeImageLoads(scene: Phaser.Scene): void {
  for (const tier of TIERS) {
    const rawKey = `tier-raw-${tier.index}`;
    if (scene.textures.exists(rawKey)) continue;
    scene.load.image(rawKey, `/shapes/tier-${tier.index}.jpeg`);
  }
}

/**
 * Call from scene.create() BEFORE ensureTierTextures.
 *
 * For each loaded raw JPEG:
 *   1. Downsample to a DPR-scaled canvas using multi-pass halving.
 *   2. Draw the procedural shape outline as an opaque white mask.
 *      This uses the SAME geometry as ensureTierTextures / physicsRadiusFor,
 *      so the visual silhouette exactly matches the physics collider.
 *   3. Composite: paint the downsampled JPEG only inside that mask.
 *
 * This completely replaces BFS / threshold background-removal. Imagen images
 * have a soft glow halo baked around every shape; thresholding that gradient
 * always produces jagged or blurry edges. Clipping to the procedural silhouette
 * gives pixel-perfect crisp edges with zero background leakage.
 */
export function bakeTierImagesIntoTextures(scene: Phaser.Scene): void {
  const DPR = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 3) : 1;

  for (const tier of TIERS) {
    const rawKey = `tier-raw-${tier.index}`;
    const destKey = textureKey(tier.index);
    if (!scene.textures.exists(rawKey)) continue;
    if (scene.textures.exists(destKey)) continue;

    const srcImg = scene.textures.get(rawKey).getSourceImage() as HTMLImageElement;
    const logicalSize = Math.round((tier.radius * 2) / FILL_FACTOR) + 6;
    const canvasSize  = Math.round(logicalSize * DPR);

    // Step 1: downsample JPEG via multi-pass halving.
    const imgCanvas = document.createElement('canvas');
    imgCanvas.width = canvasSize; imgCanvas.height = canvasSize;
    _drawHQ(imgCanvas.getContext('2d')!, srcImg, canvasSize);

    // Step 2: procedural shape mask (opaque white silhouette, transparent elsewhere).
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = canvasSize; maskCanvas.height = canvasSize;
    const maskR = Math.round(canvasSize * FILL_FACTOR / 2);
    _drawProceduralMask(maskCanvas.getContext('2d')!, tier, maskR, canvasSize / 2, canvasSize / 2);

    // Step 3: composite — Imagen texture visible only inside the procedural mask.
    const canvas = document.createElement('canvas');
    canvas.width = canvasSize; canvas.height = canvasSize;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(maskCanvas, 0, 0);           // paint mask
    ctx.globalCompositeOperation = 'source-in'; // clip subsequent draws to mask
    ctx.drawImage(imgCanvas, 0, 0);             // Imagen texture inside the shape
    ctx.globalCompositeOperation = 'source-over';

    scene.textures.addCanvas(destKey, canvas);

    // Override frame display size to logical CSS pixels while keeping the full
    // hi-DPI canvas for sharp sampling.
    const frame = scene.textures.get(destKey).get('__BASE') as Phaser.Textures.Frame & Record<string, number>;
    frame.width      = logicalSize;
    frame.height     = logicalSize;
    frame.halfWidth  = logicalSize / 2;
    frame.halfHeight = logicalSize / 2;
    frame.centerX    = logicalSize / 2;
    frame.centerY    = logicalSize / 2;
    frame.cutWidth   = canvasSize;
    frame.cutHeight  = canvasSize;
  }
}

/** Multi-pass halving so extreme downscale ratios (1024 → 50px) stay crisp. */
function _drawHQ(
  ctx: CanvasRenderingContext2D,
  src: HTMLImageElement,
  targetSize: number,
): void {
  const naturalSize = Math.max(src.naturalWidth || targetSize, src.naturalHeight || targetSize);
  let current: HTMLImageElement | HTMLCanvasElement = src;
  let currentSize = naturalSize;

  while (currentSize > targetSize * 1.5) {
    const next = Math.max(Math.round(currentSize / 2), targetSize);
    const tmp  = document.createElement('canvas');
    tmp.width  = next; tmp.height = next;
    const tCtx = tmp.getContext('2d')!;
    tCtx.imageSmoothingEnabled = true;
    tCtx.imageSmoothingQuality = 'high';
    tCtx.drawImage(current, 0, 0, next, next);
    current = tmp; currentSize = next;
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(current, 0, 0, targetSize, targetSize);
}

/**
 * Draws the procedural shape silhouette as an opaque white fill.
 * Uses the same geometry as ensureTierTextures (polygonVerts, heart/plus/crescent
 * helpers) so this mask can serve as a precise collider-aligned clipping region.
 *
 * r    – shape radius in canvas pixels (= canvasSize × FILL_FACTOR / 2)
 * cx/cy – canvas centre in pixels
 */
function _drawProceduralMask(
  ctx: CanvasRenderingContext2D,
  tier: Tier,
  r: number,
  cx: number,
  cy: number,
): void {
  ctx.fillStyle = '#ffffff';
  const sf = r / tier.radius; // scale from tier-radius space → canvas pixels

  switch (tier.kind) {
    case 'crescent': {
      // Same two-circle parameters as generateCrescentTexture.
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(cx + r * 0.45, cy - r * 0.15, r * 0.78, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
      break;
    }
    case 'plus': {
      const arm = r;
      let thick = Math.round(r * 0.42);
      if (thick % 2 !== 0) thick++;
      const half = thick / 2;
      ctx.fillRect(Math.round(cx) - arm, Math.round(cy) - half, arm * 2, thick);
      ctx.fillRect(Math.round(cx) - half, Math.round(cy) - arm, thick, arm * 2);
      break;
    }
    case 'heart': {
      const lo = r * 0.35, lr = r * 0.42, ly = cy - r * 0.2;
      const loOuter = lo + lr;
      ctx.beginPath(); ctx.arc(cx - lo, ly, lr, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(cx + lo, ly, lr, 0, Math.PI * 2); ctx.fill();
      const lmid = ly + lr * 0.3, bottom = cy + r * 0.88;
      ctx.beginPath();
      ctx.moveTo(cx - loOuter, lmid); ctx.lineTo(cx + loOuter, lmid); ctx.lineTo(cx, bottom);
      ctx.closePath(); ctx.fill();
      ctx.beginPath();
      ctx.moveTo(cx - lo, ly); ctx.lineTo(cx + lo, ly); ctx.lineTo(cx, ly + lr * 0.6);
      ctx.closePath(); ctx.fill();
      break;
    }
    case 'circle': {
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    default: {
      // triangle, square, pentagon, hexagon, octagon, star
      const verts = polygonVerts(tier.kind, tier.radius);
      if (verts.length === 0) break;
      ctx.beginPath();
      ctx.moveTo(verts[0].x * sf + cx, verts[0].y * sf + cy);
      for (let i = 1; i < verts.length; i++) ctx.lineTo(verts[i].x * sf + cx, verts[i].y * sf + cy);
      ctx.closePath();
      ctx.fill();
      break;
    }
  }
}

/**
 * Polygon vertices for the crescent C-shape physics collider.
 * Uses the same two-circle parameters as generateCrescentTexture so the
 * collider precisely matches the visual silhouette produced by _drawProceduralMask.
 *
 * Returns a closed CCW polygon suitable for Matter.Bodies.fromVertices.
 */
export function crescentPhysicsVerts(r: number): { x: number; y: number }[] {
  const bx = r * 0.45, by = -r * 0.15, br = r * 0.78;
  const STEPS = 32;
  const outer: { x: number; y: number }[] = [];
  const inner: { x: number; y: number }[] = [];

  // Outer arc: walk main circle CCW, keep points outside the bite circle.
  for (let i = 0; i < STEPS; i++) {
    const a = (i / STEPS) * Math.PI * 2;
    const x = Math.cos(a) * r, y = Math.sin(a) * r;
    const dx = x - bx, dy = y - by;
    if (dx * dx + dy * dy >= br * br) outer.push({ x, y });
  }

  // Inner arc: walk bite circle in reverse to close the C-shape polygon.
  for (let i = STEPS - 1; i >= 0; i--) {
    const a = (i / STEPS) * Math.PI * 2;
    const x = bx + Math.cos(a) * br, y = by + Math.sin(a) * br;
    if (x * x + y * y <= r * r) inner.push({ x, y });
  }

  return [...outer, ...inner];
}
